import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VideoCard from '../components/VideoCard';
import { Button } from '../components/ui/button';
import { Loader2, ChevronLeft, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const orientationConfig = {
  straight: {
    title: 'Straight',
    gradient: 'from-pink-500 to-rose-600',
    bgGradient: 'from-pink-900/20 to-rose-900/20',
    description: 'Explore the hottest straight content on VelvetStream'
  },
  gay: {
    title: 'Gay',
    gradient: 'from-blue-500 to-indigo-600',
    bgGradient: 'from-blue-900/20 to-indigo-900/20',
    description: 'Discover premium gay content from top creators'
  },
  trans: {
    title: 'Trans',
    gradient: 'from-cyan-400 to-teal-500',
    bgGradient: 'from-cyan-900/20 to-teal-900/20',
    description: 'Browse exclusive trans content and creators'
  }
};

const OrientationPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const config = orientationConfig[type] || orientationConfig.straight;

  useEffect(() => {
    setVideos([]);
    setPage(1);
    fetchVideos(1);
  }, [type]);

  const fetchVideos = async (pageNum) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/content/orientation/${type}?page=${pageNum}`);
      const newVideos = response.data.videos || [];
      
      if (pageNum === 1) {
        setVideos(newVideos);
      } else {
        setVideos(prev => [...prev, ...newVideos]);
      }
      
      setHasMore(newVideos.length >= 20);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVideos(nextPage);
  };

  return (
    <div className="min-h-screen pt-20" data-testid={`${type}-page`}>
      {/* Hero Banner */}
      <div className={`relative h-48 md:h-64 bg-gradient-to-br ${config.bgGradient} overflow-hidden`}>
        <div className="absolute inset-0 bg-[#050505]/60" />
        <div className="absolute inset-0 flex items-center px-4 md:px-8 lg:px-16">
          <div className="max-w-7xl mx-auto w-full">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <div className="flex items-center gap-4 mb-3">
              <Flame className={`w-10 h-10 bg-gradient-to-br ${config.gradient} bg-clip-text text-transparent`} />
              <h1 
                className={`text-4xl md:text-6xl font-normal tracking-wide uppercase bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                {config.title}
              </h1>
            </div>
            <p className="text-gray-400 text-lg max-w-xl">{config.description}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-8 lg:px-16 py-12">
        <div className="max-w-7xl mx-auto">
          {loading && videos.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">No videos found in this category</p>
            </div>
          ) : (
            <>
              <div className="video-grid mb-12" data-testid="orientation-video-grid">
                {videos.map((video, index) => (
                  <VideoCard key={video.id || index} video={video} />
                ))}
              </div>

              {hasMore && (
                <div className="text-center">
                  <Button
                    onClick={loadMore}
                    disabled={loading}
                    className="bg-white text-black hover:bg-gray-200 px-8"
                    data-testid="load-more-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrientationPage;
